import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'recordButton',
    'recordButtonText',
    'recordIcon',
    'sendButton',
    'rerecordButton',
    'recordingIndicator',
    'readyState',
    'readyToSendState',
    'levelMeter',
    'levelBar',
  ]

  static values = { conversationId: Number }

  connect() {
    console.log('🎙️ Audio Modal Controller connected')
    this.mediaRecorder = null
    this.chunks = []
    this.audioBlob = null
    this.isRecording = false
    this.stream = null
    this.audioContext = null
    this.analyser = null
    this.animationId = null
  }

  disconnect() {
    this.cleanup()
  }

  async toggleRecording() {
    if (this.isRecording) {
      this.stopRecording()
    } else {
      await this.startRecording()
    }
  }

  async startRecording() {
    console.log('▶️ Starting recording')

    try {
      // Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Setup MediaRecorder
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType: 'audio/webm' })
      this.chunks = []

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          this.chunks.push(e.data)
        }
      }

      this.mediaRecorder.onstop = () => {
        this.processRecording()
      }

      // Setup audio level visualization
      this.setupAudioLevelMeter()

      // Start recording
      this.mediaRecorder.start()
      this.isRecording = true

      // Update UI
      this.showRecordingState()
    } catch (error) {
      console.error('Failed to start recording:', error)
      alert('Não foi possível acessar o microfone. Por favor, verifique as permissões.')
    }
  }

  stopRecording() {
    console.log('⏹️ Stopping recording')

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop()
    }

    this.isRecording = false
    this.stopAudioLevelMeter()

    // Stop all tracks
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }
  }

  processRecording() {
    console.log('📼 Processing recording')

    // Create blob from chunks
    this.audioBlob = new Blob(this.chunks, { type: 'audio/webm' })

    // Update UI to show ready to send state
    this.showReadyToSendState()
  }

  startNewRecording() {
    console.log('🔄 Starting new recording')

    // Reset everything
    this.cleanup()
    this.showReadyState()
    this.startRecording()
  }

  async sendAudio() {
    console.log('📤 Sending audio')

    if (!this.audioBlob) {
      console.error('No audio to send')
      return
    }

    // Create FormData
    const formData = new FormData()
    formData.append('audio', this.audioBlob, 'recording.webm')
    formData.append('conversation_id', this.conversationIdValue)

    try {
      // Send to server
      const response = await fetch('/chat_manager/send_audio', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        },
        body: formData,
      })

      if (response.ok) {
        console.log('✅ Audio sent successfully')
        this.close()
      } else {
        throw new Error('Failed to send audio')
      }
    } catch (error) {
      console.error('Failed to send audio:', error)
      alert('Erro ao enviar áudio. Por favor, tente novamente.')
    }
  }

  cancel() {
    console.log('❌ Cancelling recording')

    if (this.isRecording) {
      this.stopRecording()
    }

    this.cleanup()
    this.close()
  }

  close() {
    // Close modal by removing it from DOM
    this.element.closest('.fixed.inset-0').remove()
  }

  // UI State Management
  showRecordingState() {
    // Hide ready state
    this.readyStateTarget.classList.add('hidden')
    this.readyToSendStateTarget.classList.add('hidden')

    // Show recording indicator
    this.recordingIndicatorTarget.classList.remove('hidden')
    this.levelMeterTarget.classList.remove('hidden')

    // Update record button
    this.recordButtonTarget.classList.remove('bg-blue-600', 'hover:bg-blue-700')
    this.recordButtonTarget.classList.add('bg-gray-600', 'hover:bg-gray-700')
    this.recordButtonTextTarget.textContent = 'Parar'
    this.recordIconTarget.innerHTML = '<rect x="8" y="8" width="8" height="8"></rect>'

    // Hide other buttons
    this.sendButtonTarget.classList.add('hidden')
    this.rerecordButtonTarget.classList.add('hidden')
  }

  showReadyToSendState() {
    // Hide recording indicator and ready state
    this.recordingIndicatorTarget.classList.add('hidden')
    this.readyStateTarget.classList.add('hidden')
    this.levelMeterTarget.classList.add('hidden')

    // Show ready to send state
    this.readyToSendStateTarget.classList.remove('hidden')

    // Hide record button, show other controls
    this.recordButtonTarget.classList.add('hidden')
    this.sendButtonTarget.classList.remove('hidden')
    this.sendButtonTarget.disabled = false
    this.rerecordButtonTarget.classList.remove('hidden')
  }

  showReadyState() {
    // Show ready state
    this.readyStateTarget.classList.remove('hidden')

    // Hide other states
    this.recordingIndicatorTarget.classList.add('hidden')
    this.readyToSendStateTarget.classList.add('hidden')
    this.levelMeterTarget.classList.add('hidden')

    // Reset record button
    this.recordButtonTarget.classList.remove('hidden', 'bg-gray-600', 'hover:bg-gray-700')
    this.recordButtonTarget.classList.add('bg-blue-600', 'hover:bg-blue-700')
    this.recordButtonTextTarget.textContent = 'Gravar'
    this.recordIconTarget.innerHTML = '<circle cx="12" cy="12" r="8"></circle>'

    // Hide other buttons
    this.sendButtonTarget.classList.add('hidden')
    this.rerecordButtonTarget.classList.add('hidden')
  }

  // Audio Level Meter
  setupAudioLevelMeter() {
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
    this.analyser = this.audioContext.createAnalyser()
    this.analyser.fftSize = 256

    const source = this.audioContext.createMediaStreamSource(this.stream)
    source.connect(this.analyser)

    this.updateLevelMeter()
  }

  updateLevelMeter() {
    if (!this.isRecording) return

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount)
    this.analyser.getByteFrequencyData(dataArray)

    // Calculate average level
    const average = dataArray.reduce((a, b) => a + b) / dataArray.length
    const level = Math.min(100, (average / 128) * 100)

    // Update level bar
    this.levelBarTarget.style.width = `${level}%`

    // Color based on level (keeping blue theme)
    if (level > 80) {
      this.levelBarTarget.classList.remove('bg-blue-500', 'bg-blue-600')
      this.levelBarTarget.classList.add('bg-red-500')
    } else if (level > 50) {
      this.levelBarTarget.classList.remove('bg-blue-500', 'bg-red-500')
      this.levelBarTarget.classList.add('bg-blue-600')
    } else {
      this.levelBarTarget.classList.remove('bg-blue-600', 'bg-red-500')
      this.levelBarTarget.classList.add('bg-blue-500')
    }

    this.animationId = requestAnimationFrame(() => this.updateLevelMeter())
  }

  stopAudioLevelMeter() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
  }

  cleanup() {
    // Stop recording if active
    if (this.isRecording) {
      this.stopRecording()
    }

    // Reset data
    this.chunks = []
    this.audioBlob = null

    // Stop audio level meter
    this.stopAudioLevelMeter()
  }
}
